import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import arrayToMap from "../../../../../../utils/arrayToMap";
import { replaceUnderscoreAndCapitalize } from "../../../../../../utils";
import { EMPTY_TEMPLATE } from "../../../../constants";
import { useSearchParams } from "../../../../../../hooks/searchParams";

const DEFAULT_OPTIONS = [];

const useTemplateData = ({ templates }) => {
    const { template_code } = useSearchParams();

    const [ isOpenUpdateDialog, setIsOpenUpdateDialog ] = useState(false);
    const [ templatesList, setTemplatesList ] = useState([]);
    const [ currentTemplate, setCurrentTemplate ] = useState(null);

    useEffect(
        () => {
            const template = templatesList.find((({ code }) => code === template_code));

            if(template && !currentTemplate) {
                setCurrentTemplate(template);
            }
        },
        [ template_code, templatesList, currentTemplate ]
    );

    const templateMap = useMemo(
        () => arrayToMap(templatesList, "code"),
        [ templatesList ]
    );

    const options = useMemo(
        () => {
            if(templatesList.length) {
                return templatesList.map(({ name, code }) => ({ label: name, value: code }));
            }

            return DEFAULT_OPTIONS;
        },
        [ templatesList ]
    );

    const handleChangeTemplate = useCallback(
        (value) => {
            const selectedTemplate = templateMap[value];

            setCurrentTemplate(selectedTemplate);
        },
        [ templateMap ]
    );

    const handleCreateTemplate = useCallback(
        ({ name, alternativeName, ...template }) => {
            const isHasTemplate = templatesList.find((template) => template.name === name || template.name === alternativeName);

            if(isHasTemplate) {
                toast.error("The Template with this name already exists");

                return;
            }

            const newTemplate = { ...EMPTY_TEMPLATE, ...template };
            const templateName = alternativeName || name;
            newTemplate.name = templateName;
            newTemplate.code = replaceUnderscoreAndCapitalize(templateName," ", "_").toLowerCase();

            setTemplatesList((prevState) => ([ ...prevState, newTemplate ]));
            setCurrentTemplate(newTemplate);
            setIsOpenUpdateDialog(false);
        },
        [ templatesList ]
    );

    const updateTemplates = useCallback(
        (newTemplate) => {
            const templateCode = newTemplate.code;

            const newTemplateList = templatesList.reduce(
                (acc, item) => {
                    if(item.code === templateCode) {
                        acc.push(newTemplate);
                    }else {
                        acc.push(item);
                    }

                    return acc;
                },
                []
            );

            setCurrentTemplate(newTemplate);
            setTemplatesList(newTemplateList);
        },
        [ templatesList ]
    );

    const handleUpdateTemplateSelector = useCallback(
        (selectorsList) => {
            const newTemplate = { ...currentTemplate };
            newTemplate.template_selectors = selectorsList;

            updateTemplates(newTemplate);
        },
        [ currentTemplate, updateTemplates ]
    );

    const handleDeleteTemplateSelector = useCallback(
        (indexSelector) => {
            const newTemplate = { ...currentTemplate };
            newTemplate.template_selectors =
                currentTemplate?.template_selectors.filter((selector, index) => index !== indexSelector);

            updateTemplates(newTemplate);
        },
        [ currentTemplate, updateTemplates ]
    );

    useEffect(
        () => {
            if(templates) {
                setTemplatesList(templates);
            }
        },
        [ templates ]
    );

    useEffect(
        () => () => {
            setTemplatesList([]);
            setCurrentTemplate(null);
        },
        []
    );

    return useMemo(
        () => ({
            currentTemplate,
            options,
            templatesList,
            isOpenUpdateDialog,
            onChangeTemplate: handleChangeTemplate,
            onOpenUpdateDialog: () => setIsOpenUpdateDialog(true),
            onCloseUpdateDialog: () => setIsOpenUpdateDialog(false),
            onCreateTemplate: handleCreateTemplate,
            onUpdateTemplateSelector: handleUpdateTemplateSelector,
            onDeleteTemplateSelector: handleDeleteTemplateSelector
        }),
        [
            templatesList,
            currentTemplate,
            handleChangeTemplate,
            handleCreateTemplate,
            handleUpdateTemplateSelector,
            handleDeleteTemplateSelector,
            isOpenUpdateDialog,
            options
        ]
    );
};

export default useTemplateData;
